import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, Chip } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';

const PasswordValidator = ({ password, onValidationChange, showProgress = true }) => {
  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    noCommon: false,
    noRepeating: false,
    noSequential: false,
    noHistory: true // This will be true by default since we can't check history on frontend
  });

  const [strength, setStrength] = useState(0);
  const [strengthLabel, setStrengthLabel] = useState('');

  // Common weak passwords to avoid
  const commonPasswords = [
    'password', '123456', '123456789', 'qwerty', 'abc123', 'password123',
    'admin', 'letmein', 'welcome', 'monkey', '1234567890', 'password1',
    '123123', 'dragon', 'master', 'hello', 'login', 'princess', 'sunshine',
    'football', 'iloveyou', 'welcome123', 'admin123', 'root', 'toor'
  ];

  const validatePassword = (pwd) => {
    const newRequirements = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(pwd),
      noCommon: !commonPasswords.includes(pwd.toLowerCase()),
      noRepeating: !/(.)\1{2,}/.test(pwd), // No 3+ consecutive same characters
      noSequential: !/(123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i.test(pwd),
      // History is enforced on the server. The browser cannot see old passwords,
      // so this stays passing and is not shown as a failed requirement.
      noHistory: true
    };

    setRequirements(newRequirements);

    // Calculate strength (0-10 scale) - now out of 9 requirements
    const fulfilledCount = Object.values(newRequirements).filter(Boolean).length;
    const strengthScore = Math.round((fulfilledCount / 9) * 10);
    setStrength(strengthScore);

    // Set strength label
    if (strengthScore >= 8) {
      setStrengthLabel('Strong');
    } else if (strengthScore >= 6) {
      setStrengthLabel('Good');
    } else if (strengthScore >= 4) {
      setStrengthLabel('Fair');
    } else {
      setStrengthLabel('Weak');
    }

    // Notify parent component
    if (onValidationChange) {
      onValidationChange({
        isValid: fulfilledCount >= 6, // Require at least 6 out of 9 requirements
        strength: strengthScore,
        requirements: newRequirements
      });
    }
  };

  useEffect(() => {
    validatePassword(password);
  }, [password]);

  const getStrengthColor = (score) => {
    if (score >= 8) return '#4caf50'; // Green
    if (score >= 6) return '#8bc34a'; // Light green
    if (score >= 4) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  const RequirementItem = ({ met, text }) => (
    <Box 
      display="flex" 
      alignItems="center" 
      gap={1} 
      sx={{ 
        minHeight: '24px',
        py: 0.5,
        px: 1,
        borderRadius: 1,
        backgroundColor: met ? 'rgba(76, 175, 80, 0.05)' : 'rgba(244, 67, 54, 0.05)',
        border: `1px solid ${met ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'}`
      }}
    >
      {met ? (
        <CheckCircle sx={{ color: '#4caf50', fontSize: 18, flexShrink: 0 }} />
      ) : (
        <Cancel sx={{ color: '#f44336', fontSize: 18, flexShrink: 0 }} />
      )}
      <Typography 
        variant="body2" 
        sx={{ 
          color: met ? '#4caf50' : '#f44336',
          fontWeight: met ? 600 : 400,
          fontSize: '0.875rem',
          lineHeight: 1.2
        }}
      >
        {text}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ mt: 1 }}>
      {showProgress && (
        <Box sx={{ mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Password Strength
            </Typography>
            <Chip 
              label={strengthLabel} 
              size="small"
              sx={{ 
                backgroundColor: getStrengthColor(strength),
                color: 'white',
                fontWeight: 600
              }}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={strength * 10}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: getStrengthColor(strength),
                borderRadius: 3
              }
            }}
          />
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
        Password Requirements:
      </Typography>
      
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 1 }}>
        <RequirementItem met={requirements.length} text="At least 8 characters" />
        <RequirementItem met={requirements.uppercase} text="At least one uppercase letter" />
        <RequirementItem met={requirements.lowercase} text="At least one lowercase letter" />
        <RequirementItem met={requirements.number} text="At least one number" />
        <RequirementItem met={requirements.special} text="At least one special character" />
        <RequirementItem met={requirements.noCommon} text="Not a common password" />
        <RequirementItem met={requirements.noRepeating} text="No 3+ consecutive same characters" />
        <RequirementItem met={requirements.noSequential} text="No sequential patterns (123, abc)" />
        <RequirementItem met={requirements.noHistory} text="Not one of your last 3 passwords" />
      </Box>
    </Box>
  );
};

export default PasswordValidator;
